import { pushSync } from "@/api/endpoints";
import type { PushChanges, PushRecord, PushResult, SyncCollection } from "@/types";

/**
 * Writing from the panel.
 *
 * Everything the panel saves goes through `POST /sync/push` — the same door
 * the phones use — rather than through REST endpoints of its own. A second
 * write path would have to allocate the organisation's `seq`, resolve
 * conflicts and write tombstones all over again, and a mistake in that copy
 * would not throw: it would desynchronise devices quietly and turn up weeks
 * later as records that never arrived. So the panel behaves like one more
 * client. Reading stays on `/companies` and friends, which page and search in
 * a way a delta stream cannot.
 *
 * Three rules the server cannot enforce for us:
 *
 *  - **ids are UUIDs the client generates.** An offline phone and this panel
 *    must never mint the same id, and a record has to keep one identity across
 *    device, server and every other device.
 *  - **`updatedAt` is the logical clock** that settles conflicts, not a
 *    timestamp for display. Ties go to the server, so re-sending an unchanged
 *    record is a no-op rather than churn.
 *  - **deleting means writing `deletedAt`.** A record that is really removed
 *    is invisible to every other device, which goes on showing it forever.
 *
 * And one that bites the other way: the server applies a `$set`, so a field
 * left out of the payload **keeps its old value**. Clearing something means
 * sending `null` or `""` for it explicitly. Every helper here therefore works
 * on a whole record read from the server rather than on a patch.
 */

/**
 * Records per request. The server's own ceiling is 1000
 * (`SYNC_PUSH_BATCH_LIMIT`); staying well under it leaves room for the one
 * write that fans out furthest — renaming a company re-stamps every product
 * and sample that carries its name.
 */
const BATCH_LIMIT = 500;

export const newRecordId = () => crypto.randomUUID();

const nowIso = () => new Date().toISOString();

/** Fields the server assigns; sending them back is at best ignored. */
function stripServerFields(record: Record<string, unknown>): Record<string, unknown> {
  const { seq: _seq, orgId: _orgId, ...rest } = record;
  return rest;
}

/** A brand new record, with its id and clocks filled in. */
export function created(fields: Record<string, unknown>): PushRecord {
  const now = nowIso();
  return { ...fields, _id: newRecordId(), createdAt: now, updatedAt: now, deletedAt: null };
}

/**
 * An existing record with `changes` applied and its clock advanced.
 *
 * Takes the whole record rather than the changed fields alone: the caller
 * always has it (the panel reads before it writes), and passing the whole
 * thing keeps a half-filled form from being mistaken for a deliberate blank.
 */
export function edited(
  record: Record<string, unknown> & { _id: string },
  changes: Record<string, unknown> = {}
): PushRecord {
  // `_id` is written last on purpose: it is the one field neither the spread
  // nor the caller's changes may replace.
  return { ...stripServerFields(record), ...changes, _id: record._id, updatedAt: nowIso() };
}

/** A tombstone. The record stays; `deletedAt` is what travels. */
export function deleted(record: Record<string, unknown> & { _id: string }): PushRecord {
  const now = nowIso();
  return {
    ...stripServerFields(record),
    _id: record._id,
    deletedAt: now,
    updatedAt: now,
  };
}

/** Splits a payload that exceeds one request into several, in order. */
function batches(changes: PushChanges): PushChanges[] {
  const flat: Array<[SyncCollection, PushRecord]> = [];
  for (const [collection, records] of Object.entries(changes)) {
    for (const record of records ?? []) flat.push([collection as SyncCollection, record]);
  }

  const result: PushChanges[] = [];
  for (let index = 0; index < flat.length; index += BATCH_LIMIT) {
    const chunk: PushChanges = {};
    for (const [collection, record] of flat.slice(index, index + BATCH_LIMIT)) {
      (chunk[collection] ??= []).push(record);
    }
    result.push(chunk);
  }
  return result;
}

/**
 * Sends the changes up.
 *
 * Batches go one at a time. Running them in parallel would work — the sequence
 * counter is allocated atomically — but a failure halfway through a parallel
 * fan-out leaves a set of writes nobody can describe, while in order it leaves
 * a prefix.
 */
export async function push(changes: PushChanges): Promise<PushResult> {
  let applied = 0;
  let skipped = 0;
  let cursor = 0;

  for (const batch of batches(changes)) {
    const data = await pushSync(batch);
    applied += data.applied;
    skipped += data.skipped;
    cursor = data.cursor;
  }

  return { applied, skipped, cursor };
}
